/* istanbul ignore file */

import React, { forwardRef, memo, useCallback } from 'react';
import type { TextInput } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';
import { type InterpreterFrom } from 'xstate';

import { useLocalizationContext } from '@order/Localization';

import { usePredictions } from '../../hooks';
import { type createLocationSearchMachine } from '../../machine';
import { LocationSearchField } from '../LocationSearchField';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchFieldForSearchType = memo(
  forwardRef<TextInput, LocationSearchFieldForSearchTypeProps>((props, ref) => {
    const {
      locationSearchService,
      searchString,
      deliverySearchString,
      isLoadingLocations,
      locationSearchType,
      onClear,
      onLocationSearchStringSubmit,
      onDeliverySearchStringChange,
    } = props;

    const { t } = useLocalizationContext();

    const { isEnabled: isPredictionsFeatureEnabled } = usePredictions({
      locationSearchService,
    });

    // ─── Helpers ─────────────────────────────────────────────────────────

    const onChangeText = useCallback(
      (enteredSearchString: string) => {
        if (!isPredictionsFeatureEnabled) return;

        locationSearchService.send({
          type: 'SEARCH_FOR_PREDICTIONS',
          searchString: enteredSearchString,
        });
      },
      [isPredictionsFeatureEnabled, locationSearchService],
    );

    // ─── Delivery ────────────────────────────────────────────────────────

    if (locationSearchType === 'delivery') {
      return (
        <View style={styles.container}>
          <LocationSearchField
            ref={ref}
            style={styles.field}
            value={deliverySearchString}
            isDisabled={isLoadingLocations}
            onChangeText={onDeliverySearchStringChange}
            placeholder={t('location.delivery-search-placeholder')}
          />
        </View>
      );
    }

    // ─── Pickup | Outpost ────────────────────────────────────────────────

    return (
      <View style={styles.container}>
        <LocationSearchField
          ref={ref}
          style={styles.field}
          value={searchString}
          isDisabled={isLoadingLocations}
          onChangeText={onChangeText}
          onSubmit={isPredictionsFeatureEnabled ? undefined : onLocationSearchStringSubmit} // prettier-ignore
          enterKeyHint={isPredictionsFeatureEnabled ? 'done' : 'search'}
          dataDetectorTypes={isPredictionsFeatureEnabled ? 'address' : undefined} // prettier-ignore
          onClear={onClear}
          placeholder={t('location.search-placeholder')}
        />
      </View>
    );
  }),
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  field: {
    minHeight: 42,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchFieldForSearchTypeProps = Readonly<{
  locationSearchService: InterpreterFrom<ReturnType<typeof createLocationSearchMachine>>; // prettier-ignore
  searchString: string;
  deliverySearchString: string;
  locationSearchType?: string;
  isLoadingLocations: boolean;
  onClear: () => void;
  onLocationSearchStringSubmit: (searchString: string) => void;
  onDeliverySearchStringChange: (searchString: string) => void;
}>;
